import React, { useMemo, useState } from 'react';
import { FormattedData } from './HisMovenment';
import { useTranslation } from 'react-i18next';

interface CategoryContentProps {
  data: FormattedData;
  category: string;
  selectedDate: string;
  days_open: string; // 'TODAY' | 'DAYS'
}

const COLORS: Record<string, string> = {
  sitting_up: '#D94A4A',
  sitting_on_bed_edge: '#E8941A',
  leaving_bed: '#4A90D9',
};

const START_HOUR = 8;
const TOTAL_MINUTES = 24 * 60;

const toMinutes = (timeStr: string): number => {
  const [h, m] = timeStr.split(':').map(Number);
  let mins = h * 60 + m;
  if (mins < START_HOUR * 60) mins += TOTAL_MINUTES;
  return mins - START_HOUR * 60;
};

const TIME_LABELS = [
  '08:00','10:00','12:00','14:00','16:00','18:00',
  '20:00','22:00','00:00','02:00','04:00','06:00','08:00',
];

// ── Tooltip 樣式 ──────────────────────────────────
const TooltipStyle: React.CSSProperties = {
  position: 'fixed',
  background: '#fff',
  border: '1px solid #d0d0d0',
  borderRadius: 7,
  padding: '5px 12px',
  fontSize: 13,
  color: '#333',
  pointerEvents: 'none',
  zIndex: 9999,
  whiteSpace: 'nowrap',
  boxShadow: '0 3px 10px rgba(0,0,0,0.13)',
  lineHeight: 1.5,
};

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  content: string;
}

// ── 產生 30 天日期字串陣列（最舊 → 最新） ──────────────
const buildDateRange = (selectedDate: string, days = 30): string[] => {
  const [y, m, d] = selectedDate.split('-').map(Number);
  const end = new Date(y, m - 1, d);
  const result: string[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const dt = new Date(end);
    dt.setDate(end.getDate() - i);
    result.push(
      `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
    );
  }
  return result;
};

const HisMovenmentContent: React.FC<CategoryContentProps> = ({ data, selectedDate, days_open }) => {
  const { t } = useTranslation();
  const isToday = days_open === 'TODAY';

  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false, x: 0, y: 0, content: '',
  });

  const translations: Record<string, string> = {
    sitting_up: t('GLOBAL.ALERT_STATE_12'),
    sitting_on_bed_edge: t('HISTORY_RECORD.MOBILITY_EDGE'),
    leaving_bed: t('HISTORY_RECORD.MOBILITY_OUTOFBED'),
  };

  const categories = Object.keys(data);

  // ── 通用 tooltip handlers ─────────────────────────
  const moveTooltip = (e: React.MouseEvent) =>
    setTooltip((prev) => ({ ...prev, x: e.clientX, y: e.clientY }));

  const hideTooltip = () =>
    setTooltip((prev) => ({ ...prev, visible: false }));

  // ── 計算持續時間字串（HH:MM:SS） ─────────────────────
  const getDurStr = (startStr: string, endStr: string): string => {
    const toSecs = (s: string) => {
      const [h, m, sec] = s.split(':').map(Number);
      return h * 3600 + m * 60 + (sec ?? 0);
    };
    let dur = toSecs(endStr) - toSecs(startStr);
    if (dur < 0) dur += 86400;
    const h = Math.floor(dur / 3600);
    const m = Math.floor((dur % 3600) / 60);
    const s = dur % 60;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };

  // ── 單日 tooltip ──────────────────────────────────
  const showTooltipToday = (e: React.MouseEvent, cat: string, startStr: string, endStr: string) => {
    const hour = parseInt(startStr.split(':')[0], 10);
    const [y, m, d] = selectedDate.split('-').map(Number);
    const base = new Date(y, m - 1, d);
    if (hour >= START_HOUR) base.setDate(base.getDate() - 1);
    const dateLabel = `${base.getMonth() + 1}/${base.getDate()}`;
    const content = `${translations[cat]} · ${dateLabel} ${startStr.slice(0, 8)} · ${t('HISTORY_RECORD.MOBILITY_DURATION')} ${getDurStr(startStr, endStr)}`;
    setTooltip({ visible: true, x: e.clientX, y: e.clientY, content });
  };

  // ── 多日 tooltip ──────────────────────────────────
  const showTooltipDays = (e: React.MouseEvent, cat: string, dateKey: string, count: number, totalSecs: number) => {
    const [, m, d] = dateKey.split('-').map(Number);
    const h = Math.floor(totalSecs / 3600);
    const min = Math.floor((totalSecs % 3600) / 60);
    const s = totalSecs % 60;
    const durStr = `${String(h).padStart(2,'0')}:${String(min).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    const content = `${translations[cat]} · ${m}/${d} · ${count} ${t('unit.times')} · ${t('HISTORY_RECORD.MOBILITY_TOTAL_TIME')} ${durStr}`;
    setTooltip({ visible: true, x: e.clientX, y: e.clientY, content });
  };

  // ── 摘要卡片 ──────────────────────────────────────
  const SummaryCards = () => (
    <div style={{ display: 'flex', gap: 14, padding: '20px 20px 14px' }}>
      {categories.map((cat) => (
        <div key={cat} style={{ flex: 1, background: '#daf5f7', borderRadius: 14, padding: '16px 20px' }}>
          <div style={{ fontSize: 16, color: '#2a2a2a', marginBottom: 8, fontWeight: 700 }}>
            {translations[cat]}
          </div>
          <div style={{ fontSize: 38, fontWeight: 'bold', color: '#2a2a2a', lineHeight: 1.1 }}>
            {data[cat][0]}{' '}
            <span style={{ fontSize: 22, fontWeight: 600, marginLeft: 4 }}>
              {t('HISTORY_RECORD.times') || t('unit.times')}
            </span>
          </div>
          <div style={{ fontSize: 15, color: '#2a2a2a', marginTop: 6, fontWeight: 600 }}>
            {t('HISTORY_RECORD.MOBILITY_TOTAL_TIME')} {data[cat][1]}
          </div>
        </div>
      ))}
    </div>
  );

  // ── 圖例 ──────────────────────────────────────────
  const Legend = () => (
    <div style={{ display: 'flex', gap: 20, padding: '6px 0 12px 90px', fontSize: 14 }}>
      {categories.map((cat) => (
        <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 14, height: 14, borderRadius: 3, background: COLORS[cat] ?? '#888' }} />
          <span style={{ color: '#444', fontWeight: 600 }}>{translations[cat]}</span>
        </div>
      ))}
    </div>
  );

  // ══════════════════════════════════════════════════
  // 單日模式
  // ══════════════════════════════════════════════════

  const SwimLane = ({ cat }: { cat: string }) => {
    const events: any[] = data[cat][2] ?? [];
    const color = COLORS[cat] ?? '#888';
    const midnightPct = ((16 * 60) / TOTAL_MINUTES) * 100;

    return (
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ width: 80, flexShrink: 0, fontSize: 13, color: '#333', textAlign: 'right', paddingRight: 10, fontWeight: 700 }}>
          {translations[cat]}
        </div>
        <div
          style={{ flex: 1, height: 38, background: '#daf5f7', borderRadius: 5, position: 'relative', overflow: 'hidden' }}
          onMouseLeave={hideTooltip}
        >
          {TIME_LABELS.slice(0, -1).map((_, i) => (
            <div key={i} style={{ position: 'absolute', left: `${(i / 12) * 100}%`, top: 0, bottom: 0, borderLeft: '1px solid #b8dce8' }} />
          ))}
          <div style={{ position: 'absolute', left: `${midnightPct}%`, top: 0, bottom: 0, borderLeft: '1.5px dashed #7ab8cc', zIndex: 2 }} />
          {events.map((entry: any, idx: number) => {
            const startStr: string = entry[1];
            const endStr: string = entry[4];
            if (!startStr || !endStr) return null;
            const startMin = toMinutes(startStr);
            const endMin = toMinutes(endStr);
            const duration = endMin >= startMin ? endMin - startMin : TOTAL_MINUTES - startMin + endMin;
            const leftPct = (startMin / TOTAL_MINUTES) * 100;
            const widthPct = Math.max((duration / TOTAL_MINUTES) * 100, 0.25);
            return (
              <div
                key={idx}
                style={{ position: 'absolute', left: `${leftPct}%`, width: `${widthPct}%`, minWidth: 3, top: '12%', height: '76%', background: color, borderRadius: 3, zIndex: 3, cursor: 'pointer' }}
                onMouseEnter={(e) => showTooltipToday(e, cat, startStr, endStr)}
                onMouseMove={moveTooltip}
                onMouseLeave={hideTooltip}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const TimeAxis = () => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const today = new Date(y, m - 1, d);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const fmt = (dt: Date) =>
      dt.toLocaleDateString('zh-TW', { timeZone: 'Asia/Taipei', month: 'numeric', day: 'numeric' });
    return (
      <div style={{ display: 'flex', marginLeft: 80, borderTop: '1px solid #b8dce8', paddingTop: 6 }}>
        {TIME_LABELS.map((label, i) => (
          <div key={i} style={{ flex: i < TIME_LABELS.length - 1 ? 1 : 0, fontSize: 12, color: '#444', fontWeight: 700, position: 'relative' }}>
            <span style={{ position: 'absolute', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>{label}</span>
            {i === 0 && (
              <span style={{ position: 'absolute', top: 18, transform: 'translateX(-50%)', fontSize: 11, fontWeight: 600, color: '#444', whiteSpace: 'nowrap' }}>
                {fmt(yesterday)}
              </span>
            )}
            {label === '00:00' && (
              <span style={{ position: 'absolute', top: 18, transform: 'translateX(-50%)', fontSize: 11, color: '#444', whiteSpace: 'nowrap', fontWeight: 700 }}>
                {fmt(today)}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ══════════════════════════════════════════════════
  // 多日模式（方案 A：X 軸 = 日期，長條高度 = 當日次數）
  // ══════════════════════════════════════════════════

  const dateRange = useMemo(() => buildDateRange(selectedDate, 30), [selectedDate]);

  // 計算每個 category 每天的次數與總秒數
  const dailyStats = useMemo(() => {
    const result: Record<string, Record<string, { count: number; totalSecs: number }>> = {};
    categories.forEach((cat) => {
      result[cat] = {};
      const events: any[] = data[cat][2] ?? [];
      events.forEach((entry) => {
        const dateKey: string = entry[0]?.slice(0, 10);
        const startStr: string = entry[1];
        const endStr: string = entry[4];
        if (!dateKey || !startStr || !endStr) return;
        const toSecs = (s: string) => {
          const [h, m, sec] = s.split(':').map(Number);
          return h * 3600 + m * 60 + (sec ?? 0);
        };
        let dur = toSecs(endStr) - toSecs(startStr);
        if (dur < 0) dur += 86400;
        if (!result[cat][dateKey]) result[cat][dateKey] = { count: 0, totalSecs: 0 };
        result[cat][dateKey].count += 1;
        result[cat][dateKey].totalSecs += dur;
      });
    });
    return result;
  }, [categories, data]);

  // 所有 category 的當日最大次數（用於統一縮放 Y 軸）
  const globalMax = useMemo(() => {
    let max = 1;
    categories.forEach((cat) => {
      dateRange.forEach((dateKey) => {
        const count = dailyStats[cat]?.[dateKey]?.count ?? 0;
        if (count > max) max = count;
      });
    });
    return max;
  }, [categories, dailyStats, dateRange]);

  // 多日單條泳道（長條圖）
  const DaysSwimLane = ({ cat }: { cat: string }) => {
    const color = COLORS[cat] ?? '#888';
    return (
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ width: 80, flexShrink: 0, fontSize: 13, color: '#333', textAlign: 'right', paddingRight: 10, fontWeight: 700 }}>
          {translations[cat]}
        </div>
        <div
          style={{ flex: 1, height: 56, background: '#daf5f7', borderRadius: 5, display: 'flex', alignItems: 'flex-end', padding: '4px 0', gap: 1, overflow: 'hidden' }}
          onMouseLeave={hideTooltip}
        >
          {dateRange.map((dateKey) => {
            const stat = dailyStats[cat]?.[dateKey];
            const count = stat?.count ?? 0;
            const totalSecs = stat?.totalSecs ?? 0;
            const heightPct = count > 0 ? Math.max((count / globalMax) * 88, 8) : 0;
            return (
              <div
                key={dateKey}
                style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: '100%', cursor: count > 0 ? 'pointer' : 'default' }}
                onMouseEnter={count > 0 ? (e) => showTooltipDays(e, cat, dateKey, count, totalSecs) : undefined}
                onMouseMove={count > 0 ? moveTooltip : undefined}
                onMouseLeave={count > 0 ? hideTooltip : undefined}
              >
                {count > 0 && (
                  <div style={{ width: '75%', background: color, borderRadius: '2px 2px 0 0', height: `${heightPct}%`, minHeight: 3 }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // 多日 X 軸（日期）
  const DaysAxis = () => {
    return (
      <div style={{ display: 'flex', marginLeft: 80, borderTop: '1px solid #b8dce8', paddingTop: 6 }}>
        {dateRange.map((dateKey, i) => {
          const [, m, d] = dateKey.split('-').map(Number);
          // 每5格或頭尾顯示日期
          const showLabel = i === 0 || i === dateRange.length - 1 || i % 5 === 4;
          return (
            <div key={dateKey} style={{ flex: 1, fontSize: 10, color: '#555', fontWeight: 600, position: 'relative', height: 28 }}>
              {showLabel && (
                <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
                  {`${m}/${d}`}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ── 主渲染 ────────────────────────────────────────
  return (
    <div
      className="fx-d-c h-95 w-100 border_bgC _SE_top"
      style={{ overflowY: 'hidden', borderRadius: 16 }}
      onMouseLeave={hideTooltip}
    >
      <SummaryCards />
      <div style={{ padding: '4px 20px 64px' }} onMouseLeave={hideTooltip}>
        <Legend />
        {isToday
          ? categories.map((cat) => <SwimLane key={cat} cat={cat} />)
          : categories.map((cat) => <DaysSwimLane key={cat} cat={cat} />)
        }
        {isToday ? <TimeAxis /> : <DaysAxis />}
      </div>

      {/* 自訂 Tooltip */}
      {tooltip.visible && (
        <div style={{ ...TooltipStyle, left: tooltip.x + 14, top: tooltip.y - 42 }}>
          {tooltip.content}
        </div>
      )}
    </div>
  );
};

export default HisMovenmentContent;